import { useEffect, useMemo, useRef, useState } from "react";

import termsOfService from "../../pages/data/legal/terms.json";
import privacyPolicy from "../../pages/data/legal/privacy.json";
import imprint from "../../pages/data/legal/imprint.json";
import serviceLevelAgreement from "../../pages/data/legal/sla.json";

import Container from "../ui/Container";
import SectionTitle from "../ui/SectionTitle";
import Chip from "../ui/Chip";

function normalizeDoc(doc) {
  // Expected shapes supported:
  // 1) { title, updated, sections: [{ heading, content: [string|{type,...}] }]}
  // 2) { title, updated, content: [ ... ] }  (fallback)
  if (!doc) return null;
  const title = doc.title || "<Untitled Document>";
  const updated = doc.updated || doc.lastUpdated || "";
  const sections = Array.isArray(doc.sections)
    ? doc.sections
    : Array.isArray(doc.content)
      ? [{ heading: "", content: doc.content }]
      : [];
  return { title, updated, sections };
}

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function renderContentBlock(block, key) {
  // Supports:
  // - string => paragraph
  // - { type: "p"|"h3"|"ul"|"ol"|"quote", text, items }
  // - { type: "divider" }
  if (typeof block === "string") {
    return (
      <p key={key} className="legal-p">
        {block}
      </p>
    );
  }

  if (!block || typeof block !== "object") return null;

  switch (block.type) {
    case "h3":
      return (
        <h3 key={key} className="legal-h3">
          {block.text}
        </h3>
      );
    case "quote":
      return (
        <blockquote key={key} className="legal-quote">
          {block.text}
        </blockquote>
      );
    case "ul":
      return (
        <ul key={key} className="legal-ul">
          {(block.items || []).map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="legal-ol">
          {(block.items || []).map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ol>
      );
    case "divider":
      return <hr key={key} className="legal-hr" />;
    case "p":
    default:
      return (
        <p key={key} className="legal-p">
          {block.text}
        </p>
      );
  }
}

function LegalDoc({ doc, id }) {
  const normalized = normalizeDoc(doc);

  if (!normalized || !normalized.sections?.length) return null;

  return (
    <section id={id} className="scroll-mt-28">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8 shadow-[0_0_0_1px_rgba(255,255,255,0.02)]">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
              {normalized.title}
            </h2>
            {normalized.updated ? (
              <p className="text-sm text-white/50 mt-1">
                Last updated: {normalized.updated}
              </p>
            ) : null}
          </div>

          <div className="mt-3 md:mt-0 text-xs text-white/40">
            XA Hosting Legal
          </div>
        </div>

        <div className="mt-6 space-y-8">
          {normalized.sections.map((s, idx) => {
            const heading = s.heading || "";
            const content = Array.isArray(s.content) ? s.content : s.content ? [s.content] : [];
            const subId =
              heading ? `${id}-${slugify(heading)}` : `${id}-section-${idx}`;

            return (
              <div key={subId} id={subId} className="scroll-mt-28">
                {heading ? <h3 className="legal-h2">{heading}</h3> : null}
                <div className="mt-3 space-y-3">
                  {content.map((block, i) => renderContentBlock(block, `${subId}-${i}`))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function LegalSections() {
  const sections = useMemo(() => {
    const list = [
      { key: "terms", label: "Terms", doc: termsOfService, id: "terms" },
      { key: "privacy", label: "Privacy", doc: privacyPolicy, id: "privacy" },
      { key: "imprint", label: "Imprint", doc: imprint, id: "imprint" },
      { key: "sla", label: "SLA", doc: serviceLevelAgreement, id: "sla" }
    ];

    // Filter out empty docs
    return list.filter((x) => normalizeDoc(x.doc)?.sections?.length);
  }, []);

  const [active, setActive] = useState(sections[0]?.key || "terms");
  const observerRef = useRef(null);

  useEffect(() => {
    if (!sections.length) return;

    const ids = sections.map((s) => s.id);

    observerRef.current?.disconnect?.();
    const obs = new IntersectionObserver(
      (entries) => {
        // pick the most visible entry
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];

        if (visible?.target?.id) {
          const matched = sections.find((s) => s.id === visible.target.id);
          if (matched) setActive(matched.key);
        }
      },
      { root: null, threshold: [0.15, 0.25, 0.4, 0.6] }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    observerRef.current = obs;
    return () => obs.disconnect();
  }, [sections]);

  const scrollTo = (id, key) => {
    setActive(key);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="bg-[#070A0F] pb-24 pt-10">
      <Container>
        <SectionTitle eyebrow="Legal Policies" />

        {/* Sticky quick nav */}
        <div className="sticky top-16 z-20 mt-6">
          <div className="rounded-2xl border border-white/10 bg-[#070A0F]/70 backdrop-blur-xl p-3">
            <div className="flex flex-wrap gap-2 justify-center">
              {sections.map((s) => (
                <div key={s.key} onClick={() => scrollTo(s.id, s.key)} className="cursor-pointer">
                  <Chip
                    className={
                      active === s.key
                        ? "border-cyan-400/40 bg-cyan-400/10 text-white"
                        : "border-white/10 bg-white/5 text-white/70 hover:text-white"
                    }
                  >
                    {s.label}
                  </Chip>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Page intro */}
        <div className="mt-10 mb-10">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-6 md:p-8">
            <p className="text-white/70 leading-relaxed">
              This page contains the legal policies for XA Hosting, including our Terms of Service,
              Privacy Policy, Imprint, and Service Level Agreement. Use the tabs above to jump
              between sections.
            </p>
          </div>
        </div>

        {/* Legal docs */}
        <div className="space-y-10">
          {sections.map((s) => (
            <LegalDoc key={s.key} doc={s.doc} id={s.id} />
          ))}
        </div>
      </Container>

      {/* Local styles (Tailwind classes) */}
      <style>{`
        .legal-h2 { 
          font-size: 1.125rem;
          line-height: 1.6;
          font-weight: 600;
          color: rgba(255,255,255,0.95);
        }
        .legal-h3 {
          font-size: 1rem;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          margin-top: 1.25rem;
        }
        .legal-p {
          color: rgba(255,255,255,0.7);
          line-height: 1.75;
          font-size: 0.95rem;
        }
        .legal-ul, .legal-ol {
          color: rgba(255,255,255,0.7);
          line-height: 1.75;
          font-size: 0.95rem;
          padding-left: 1.25rem;
        }
        .legal-ul { list-style: disc; }
        .legal-ol { list-style: decimal; }
        .legal-quote {
          border-left: 2px solid rgba(34,211,238,0.35);
          padding-left: 1rem;
          color: rgba(255,255,255,0.75);
          line-height: 1.75;
          background: rgba(255,255,255,0.03);
          border-radius: 0.75rem;
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
          padding-right: 0.75rem;
        }
        .legal-hr {
          border: 0;
          height: 1px;
          background: rgba(255,255,255,0.08);
          margin: 1.25rem 0;
        }
      `}</style>
    </section>
  );
}
